import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Send as SendIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';

const InviteDialog = ({
  open,
  onClose,
  onSend,
  onResend,
  onCancel,
  person,
  location,
  saving
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const isPending = person?.inviteStatus === 'pending';
  const isExpired = person?.inviteStatus === 'expired';

  useEffect(() => {
    if (person) {
      // Pre-fill email from person's email or data
      setEmail(person.email || person.data?.email || '');
    }
    setError('');
  }, [person, open]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSend = () => {
    if (!email.trim()) {
      setError('Email address is required');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setError('');

    if (isPending) {
      onResend?.(person.id);
    } else {
      onSend?.(person.id, { email: email.trim() });
    }
  };

  const handleCancel = () => {
    onCancel?.(person.id);
  };

  if (!person) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isPending ? 'Resend Invitation' : 'Invite to CARO Portal'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Person Info */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              mb: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="action" />
              <Typography variant="body1" fontWeight="medium">
                {person.friendlyName}
              </Typography>
            </Box>
            {location && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon color="action" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  {location.friendlyName}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Status Alert for Pending/Expired */}
          {isPending && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                An invitation is already pending for this person.
                {person.inviteExpires && (
                  <> Expires on {new Date(person.inviteExpires).toLocaleDateString()}.</>
                )}
              </Typography>
            </Alert>
          )}

          {isExpired && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                The previous invitation has expired. Send a new invite to this email address.
              </Typography>
            </Alert>
          )}

          {/* Email Input */}
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError('');
            }}
            error={!!error}
            helperText={error}
            placeholder="person@example.com"
            InputProps={{
              startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />
            }}
            disabled={saving}
          />

          {/* Info Message */}
          <Alert severity="info" sx={{ mt: 3 }} icon={false}>
            <Typography variant="body2">
              An invitation email will be sent to this address. The recipient will be able
              to create a CARO Portal account using Microsoft SSO. The invite link will be
              valid for 7 days.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isPending && (
          <Button
            onClick={handleCancel}
            color="error"
            disabled={saving}
            sx={{ mr: 'auto' }}
          >
            Cancel Invite
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SendIcon />}
          disabled={saving}
        >
          {saving ? 'Sending...' : isPending ? 'Resend Invite' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteDialog;
